/**
 * Subscription Success Page
 * 
 * Shows success message after successful Stripe checkout
 */

import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import Layout from '../../components/Layout';
import { useSubscription } from '../../context/subscription/SubscriptionContext';

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSubscription, currentSubscription } = useSubscription();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Refresh subscription data to get the updated plan (only once on mount)
    refreshSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  const handleContinue = () => {
    navigate('/build-audience');
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto text-center py-12">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>

        {/* Success Message */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Subscription Activated!
        </h1>
        
        <p className="text-gray-600 mb-8">
          Your subscription has been successfully activated. You now have access to all the features in your plan.
        </p>

        {/* Current Plan Info */}
        {currentSubscription && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <h3 className="font-medium text-blue-900 mb-2">
              Current Plan: {currentSubscription.name}
            </h3>
            <p className="text-sm text-blue-700">
              Status: {currentSubscription.status}
            </p>
          </div>
        )}

        {/* Session ID for reference */}
        {sessionId && (
          <div className="bg-gray-50 rounded-lg p-3 mb-8">
            <p className="text-xs text-gray-500 mb-1">Session ID:</p>
            <p className="text-xs font-mono text-gray-700">{sessionId}</p>
          </div>
        )}

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
        >
          Continue to Build Audience
          <ArrowRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </Layout>
  );
};

export default SubscriptionSuccess;